import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Login } from './components/Login';
import { BiometricOffer, DECLINED_KEY } from './components/BiometricOffer';
import { QrApprove } from './components/QrApprove';
import { MagicLinkVerify } from './components/MagicLinkVerify';
import { Dashboard } from './components/Dashboard';
import { UpdatePrompt } from './components/UpdatePrompt';
import { AUTH_URL } from './utils/authApi';
import { getDeviceToken, rememberUsername } from './utils/auth';
import { isMobileDevice, platformBioBeschikbaar } from './utils/deviceAuth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8009';

// Global handler (set by App component)
let globalLogoutHandler = null;

// Setup axios interceptors
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Logout when OUR Node.js backend rejects the token.
      // Our backend returns specific codes: NO_TOKEN, TOKEN_INVALID,
      // TOKEN_EXPIRED, SESSION_EXPIRED. Laravel KB-token endpoints use
      // different response shapes so they won't match.
      const code = error.response?.data?.code;
      const ourAuthCodes = ['NO_TOKEN', 'TOKEN_INVALID', 'TOKEN_EXPIRED', 'SESSION_EXPIRED'];
      if (ourAuthCodes.includes(code)) {
        localStorage.removeItem('device_token');
        localStorage.removeItem('token');
        if (globalLogoutHandler) {
          globalLogoutHandler();
        }
        // Swallow the error: UI redirects to login, no console noise.
        return new Promise(() => {});
      }
    }

    return Promise.reject(error);
  }
);

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [showBiometricOffer, setShowBiometricOffer] = useState(false);

  // Setup global handlers for axios interceptors
  useEffect(() => {
    globalLogoutHandler = () => {
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      // The interceptor swallows the 401, so an awaiting start-up check never reaches its
      // finally: without this an expired token left the app on "Loading..." for good.
      setIsLoading(false);
    };

    return () => {
      globalLogoutHandler = null;
    };
  }, []);

  useEffect(() => {
    // A magic link that finished at Laravel returns here with a one-shot code.
    // Trade it for the device token before anything else looks at storage.
    const handoff = new URLSearchParams(window.location.search).get('handoff');
    if (handoff) {
      exchangeHandoff(handoff);
      return;
    }

    // One key: an old `token` is moved to `device_token` and checked like any other.
    const storedToken = getDeviceToken();
    if (storedToken) {
      localStorage.setItem('device_token', storedToken);
      verifyToken(storedToken);
    } else {
      setIsLoading(false);
    }
    // Bewust alleen bij mount: de handoff-code is single-use en mag precies één keer
    // ingewisseld worden. Een herhaling zou een verbruikte code opnieuw aanbieden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exchangeHandoff = async (code) => {
    try {
      const response = await axios.post(`${AUTH_URL}/api/auth/handoff`, { code });

      if (response.data.success) {
        handleLogin(response.data.device_token, response.data.user);
        // Regel 7 van kb/patterns/havun-mobile-login.md: dit is het moment om
        // biometrie aan te bieden. Onderzoeken doen we ná de login, zodat een
        // trage credential-lijst het dashboard niet ophoudt.
        offerBiometrics(response.data.device_token);
      }
    } catch {
      // A spent or expired code is not an error worth showing -- the login
      // screen below is the answer either way.
    } finally {
      // Drop the code from the address bar so a reload cannot replay it and it
      // stays out of history and bookmarks.
      window.history.replaceState({}, '', window.location.pathname);
      setIsLoading(false);
    }
  };

  // Alleen aanbieden wat kan slagen: een smartphone met platform-authenticator,
  // die nog geen passkey heeft en het aanbod niet eerder wegklikte.
  const offerBiometrics = async (deviceToken) => {
    if (localStorage.getItem(DECLINED_KEY)) return;
    if (!isMobileDevice()) return;
    if (!(await platformBioBeschikbaar())) return;

    try {
      const response = await axios.get(`${AUTH_URL}/api/auth/webauthn/credentials`, {
        headers: { Authorization: `Bearer ${deviceToken}` },
      });
      if ((response.data.credentials || []).length === 0) setShowBiometricOffer(true);
    } catch {
      // Weten we het niet, dan vragen we niet. Een voorstel bovenop een bestaande
      // passkey is verwarrender dan geen voorstel.
    }
  };

  const forgetToken = () => {
    localStorage.removeItem('device_token');
    localStorage.removeItem('token');
  };

  const verifyToken = async (deviceToken) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/auth/session`,
        { headers: { Authorization: `Bearer ${deviceToken}` } }
      );

      if (response.data.valid) {
        setToken(deviceToken);
        setUser(response.data.user);
        setIsAuthenticated(true);
      } else {
        forgetToken();
      }
    } catch (err) {
      // Only a refusal (401) means the token is gone. A network hiccup or a backend restart is
      // not a verdict: keep the token and carry on -- the next request tells (25-10-2026).
      if (err.response?.status === 401) {
        forgetToken();
      } else {
        setToken(deviceToken);
        setIsAuthenticated(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Every login path ends here (QR claim, handoff, passkey, recovery code), so the name for the
  // next passkey login is remembered once, for all of them.
  const handleLogin = (newToken, userData) => {
    rememberUsername(userData);
    localStorage.setItem('device_token', newToken);
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);

    setIsAuthenticated(true);

    // Check for app updates after login
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then(registration => {
        registration.update();
      });
    }
  };

  const handleLogout = async () => {
    const deviceToken = localStorage.getItem('device_token');
    if (deviceToken) {
      try {
        await axios.post(
          `${API_URL}/api/auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${deviceToken}` } }
        );
      } catch (err) {
        // Ignore logout errors
      }
    }

    localStorage.removeItem('device_token');
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-primary-50 to-primary-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  if (showBiometricOffer) {
    return <BiometricOffer token={token} onDone={() => setShowBiometricOffer(false)} />;
  }

  return <Dashboard token={token} user={user} onLogout={handleLogout} />;
}

function App() {
  return (
    <BrowserRouter>
      <UpdatePrompt />
      <Routes>
        {/* QR approve page — phone opens this after scanning */}
        <Route path="/qr/:code" element={<QrApprove />} />
        {/* Magic link verify — email link opens this */}
        <Route path="/magic/:token" element={<MagicLinkVerify />} />
        {/* All other routes → main app */}
        <Route path="*" element={<AppContent />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
